import React, {
  useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useDefaultWorkspaceForUser,
  useWorkspaceInfoById,
  useWorkspacesForUser,
} from '../../../utils/hooks/use_workspaces_for_users';
import useClientContext from '../../contexts/ClientData/ClientData';
import useLocalStorage from '../../../utils/hooks/use_local_storage';
import useUserContext from '../../contexts/UserData/UserData';

const WorkspaceContext = React.createContext({
  loading: false,
  isSelectedWorkspaceLoading: false,
  currentWorkspaceId: null,
  currentWorkspace: null,
  defaultWorkspace: null,
  error: null,
  currentWorkspaceError: null,
  workspaceUsers: [],
  workspaces: [],
  setCurrentWorkspaceId: (_id) => { },
  addWorkspace: () => { },
  addUserToWorkspace: () => { },
  updateUserRole: () => { },
  removeUserFromWorkspace: () => { },
})

export const useWorkspaceContext = () => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();

  const [savedWorkspaceId, setSavedWorkspaceId] = useLocalStorage('saved-workspace', null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(savedWorkspaceId);
  const { isAdmin, userData } = useUserContext()
  const { isAdminDashboardEnabled } = useClientContext();
  const isRootAdmin = userData?.username === 'admin';

  // We suppress workspace routing for the root admin user because they are not meant to access the Platform User Dashboard - only the Admin Dashboard
  const suppressWorkspaceRouting = isRootAdmin && isAdminDashboardEnabled;

  const { workspace: defaultWorkspace } = useDefaultWorkspaceForUser(userData?.id, userData?.email)

  const {
    workspace: currentWorkspace,
    workspaceUsers,
    loading: isSelectedWorkspaceLoading,
    error: currentWorkspaceError,
  } = useWorkspaceInfoById(currentWorkspaceId);

  const {
    userWorkspaces,
    loading,
    error,
    addWorkspace,
    addUserToWorkspace,
    updateUserRole,
    removeUserFromWorkspace,
  } = useWorkspacesForUser(userData?.id);

  const urlPathnameArr = location.pathname.split('/');
  const workspaceInvitationId = urlPathnameArr[1] === 'workspaces' ? parseInt(urlPathnameArr[2], 10) : null;
  const isAdminPage = urlPathnameArr[1] === 'admin';

  const handleSetWorkspace = useCallback((workspaceId) => {
    if (workspaceId) {
      setSavedWorkspaceId(workspaceId);
      setCurrentWorkspaceId(workspaceId);

      if (!isAdminPage) {
        history.replace(`/workspaces/${workspaceId}`);
      }
    }
  }, [setSavedWorkspaceId, isAdminPage, history]);

  const defaultWorkspaceId = defaultWorkspace?.id;

  // If we have neither a current nor saved workspace, use the default workspace.
  useEffect(() => {
    if (!currentWorkspaceId && !savedWorkspaceId && defaultWorkspaceId && !suppressWorkspaceRouting) {
      handleSetWorkspace(defaultWorkspaceId)
    }
  }, [currentWorkspaceId, savedWorkspaceId, defaultWorkspaceId, handleSetWorkspace, suppressWorkspaceRouting])

  // If the current workspace id isn't in the list of workspaces for that user, use the default workspace
  // Fallback for a user being removed from their workspace.
  // Does not apply to Admin Users - they should have access to any workspace
  useEffect(() => {
    const existingWorkspace = userWorkspaces.find(({ id }) => id === currentWorkspaceId);
    if (!existingWorkspace && !suppressWorkspaceRouting && !isAdmin) {
      handleSetWorkspace(defaultWorkspaceId);
    }
  }, [currentWorkspaceId, userWorkspaces, defaultWorkspaceId, handleSetWorkspace, suppressWorkspaceRouting, isAdmin])

  // If we have a well-formed Workspace URL at the root, set the current workspace, else use the default workspace.
  useEffect(() => {
    if (suppressWorkspaceRouting) {
      return;
    }
    if (workspaceInvitationId) {
      // If we aren't currently in this workspace, update the current workspace.
      if (workspaceInvitationId !== currentWorkspaceId) {
        handleSetWorkspace(workspaceInvitationId);
      }
    } else if (!isAdminPage) {
      handleSetWorkspace(defaultWorkspaceId)
    }
  }, [handleSetWorkspace, workspaceInvitationId, currentWorkspaceId, defaultWorkspaceId, isAdminPage, suppressWorkspaceRouting])

  const workspaceContextValue = useMemo(() => ({
    currentWorkspaceId,
    currentWorkspace,
    currentWorkspaceError,
    setCurrentWorkspaceId: handleSetWorkspace,
    loading,
    isSelectedWorkspaceLoading,
    error,
    workspaceUsers,
    workspaces: userWorkspaces,
    defaultWorkspace,
    addWorkspace,
    addUserToWorkspace,
    updateUserRole,
    removeUserFromWorkspace,
  }), [
    currentWorkspaceId,
    currentWorkspace,
    currentWorkspaceError,
    handleSetWorkspace,
    loading,
    isSelectedWorkspaceLoading,
    error,
    workspaceUsers,
    userWorkspaces,
    defaultWorkspace,
    addWorkspace,
    addUserToWorkspace,
    updateUserRole,
    removeUserFromWorkspace,
  ])

  return (
    <WorkspaceContext.Provider value={workspaceContextValue}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export default useWorkspaceContext;
